import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  InvoiceStatus,
  InvoiceSystemTypeEnum,
  InvoiceTypeEnum,
} from '@nxsys/constants/AppEnums';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { roles } from '@nxsys/store/constants';
import { Icons } from '@nxsys/types/icons';
import { Invoice } from '@nxsys/types/models/invoice';
import { formatStatus, showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { toNumber } from 'lodash';
import moment from 'moment';

export const getColumns = ({
  messages,
  theme,
  handlePreviewInvoice,
  handleSendInvoice,
  invoiceType,
  isBureau = false,
  isBureauEmployer = false,
  role,
  handleSendMailRaiseInvoice,
  handleViewInvoiceDetail,
  handleViewCreditNoteDetail,
  payrollCountry,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  theme: any;
  handlePreviewInvoice: (id: string) => void;
  handleSendInvoice: (ids: string[]) => void;
  invoiceType: InvoiceTypeEnum;
  isBureau: boolean;
  isBureauEmployer: boolean;
  role: string;
  handleSendMailRaiseInvoice?: (id: string) => void;
  handleViewInvoiceDetail?: (id: string) => void;
  handleViewCreditNoteDetail?: (id: string) => void;
  payrollCountry?: any;
  downloadLoadingId?: string | null;
}): ColumnsType<Invoice> => {
  return [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-[8px]">
          <li
            onClick={() =>
              invoiceType === InvoiceTypeEnum.INVOICE
                ? handleViewInvoiceDetail?.(record?.id)
                : handleViewCreditNoteDetail?.(record?.id)
            }
          >
            <Tooltip title="View">
              <span><Icons.IconEye /></span>
            </Tooltip>
          </li>
          <li
            className={downloadLoadingId === record?.id ? 'pointer-events-none text-primary-500' : ''}
            onClick={() => handlePreviewInvoice(record?.id)}
          >
            <Tooltip title={downloadLoadingId === record?.id ? 'Loading...' : 'Download'}>
              <span>
                {downloadLoadingId === record?.id ? (
                  <LoadingOutlined style={{ fontSize: 16 }} spin />
                ) : (
                  <Icons.IconDownload />
                )}
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    ...((role !== roles.agency
      ? [
          {
            title: messages['common.status'],
            fixed: 'left',
            align: 'center',
            render: (record) => {
              const isOutstanding =
                record?.invoiceStatus === InvoiceStatus.INVOICED &&
                toNumber(record?.outstanding) > 0;
              const isPaid =
                record?.invoiceStatus === InvoiceStatus.INVOICED &&
                toNumber(record?.outstanding) <= 0;
              const statusLabel = isOutstanding
                ? 'Outstanding'
                : isPaid
                  ? 'Paid'
                  : `${record?.invoiceStatus?.[0]?.toUpperCase()}${record?.invoiceStatus.slice(1).toLowerCase()}`;
              const isSending =
                record?.invoiceStatus === InvoiceStatus.CREDITED
                  ? record?.sendingMailCreditNote
                  : record?.sendingMailInvoice;
              const isSentMailError = record?.isSentMailError;
              const isSentMail = record?.sentMail;
              const getDefaultInvoiceMailTooltip = () => {
                if (isSending) return messages['common.sending'];

                if (isSentMailError) {
                  if (record?.reasonSentMailError) {
                    return `${messages['common.error']}: ${record?.reasonSentMailError}`;
                  }
                  return messages['common.sentMailError'];
                }

                if (isSentMail) return messages['common.sentMail'];

                return messages['common.sendMailInvoice'];
              };

              const getNonDefaultInvoiceMailTooltip = () => {
                if (record?.sentMail && record?.isSentMailError === false) {
                  return messages['common.sentMail'];
                }

                if (record?.isSentMailError) {
                  if (record?.reasonSentMailError) {
                    return `${messages['common.error']}: ${record?.reasonSentMailError}`;
                  }
                  return messages['common.sentMailError'];
                }

                return messages['common.sendMailInvoice'];
              };

              const renderMailIcon = (
                onClick: () => void,
                tooltipTitle: string,
              ) => (
                <Tooltip title={tooltipTitle}>
                  <div className="h-[20px] cursor-pointer" onClick={onClick}>
                    {isSending ? (
                      <Icons.IconEmail color={theme.palette.orange[300]} />
                    ) : isSentMailError ? (
                      <Icons.IconEmailSetting color={theme.palette.red[500]} />
                    ) : isSentMail ? (
                      <Icons.IconEmail color={theme.palette.green[500]} />
                    ) : (
                      <Icons.IconEmail color={theme.palette.gray[500]} />
                    )}
                  </div>
                </Tooltip>
              );

              return (
                <div className="flex justify-between items-center">
                  {statusLabel ? (
                    <AppTagStatus
                      className={`tag-popup mr-0 ${formatStatus(statusLabel)}`}
                    >
                      {formatStatus(statusLabel)}
                    </AppTagStatus>
                  ) : (
                    <div></div>
                  )}
                  {!isBureauEmployer && (
                    <>
                      {!isBureau ? (
                        <>
                          {record?.invoiceType === InvoiceSystemTypeEnum.DEFAULT
                            ? renderMailIcon(
                                () => handleSendInvoice([record?.id]),
                                getDefaultInvoiceMailTooltip(),
                              )
                            : renderMailIcon(
                                () => handleSendInvoice([record?.id]),
                                getNonDefaultInvoiceMailTooltip(),
                              )}
                        </>
                      ) : (
                        <>
                          {record?.invoiceType === InvoiceSystemTypeEnum.DEFAULT
                            ? renderMailIcon(
                                () => handleSendInvoice([record?.id]),
                                getDefaultInvoiceMailTooltip(),
                              )
                            : renderMailIcon(
                                () => handleSendMailRaiseInvoice?.(record?.id),
                                getNonDefaultInvoiceMailTooltip(),
                              )}
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            },
            width: 184,
          },
        ]
      : []) as ColumnsType<Invoice>),
    ...((invoiceType === InvoiceTypeEnum.INVOICE
      ? [
          {
            title: messages['invoices.invoiceNumber'],
            key: 'invoiceNumber',
            render: (record) =>
              record?.specicalId ??
              (isBureau ? record?.bureauInvoiceNo : record?.invoiceNo) ??
              record.id,
          },
          {
            key: 'invoicedDate',
            title: messages['common.date'],
            dataIndex: 'invoicedDate',
            align: 'right',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            render: (value) =>
              value
                ? moment(value).tz('Europe/London').format('DD/MM/YYYY')
                : '_',
          },
        ]
      : [
          {
            title: messages['invoices.creditNo'],
            key: 'creditNo',
            render: (record) =>
              (isBureau ? record?.bureauCreditNo : record?.creditNo) ??
              record.id,
          },
          {
            key: 'creditDate',
            title: messages['common.date'],
            dataIndex: 'creditDate',
            align: 'right',
            render: (value) =>
              value
                ? moment(value).tz('Europe/London').format('DD/MM/YYYY')
                : '_',
          },
        ]) as ColumnsType<Invoice>),
    {
      key: 'companyRef',
      title: messages['common.from'],
      dataIndex: 'companyRef',
      render: (value) =>
        !isBureau ? value?.companyName : value?.bureauAdmin?.company || '-',
      width: 350,
    },
    {
      key: 'to',
      title: messages['common.to'],
      render: (value) =>
        !isBureau
          ? value?.agencyRef?.name
          : value?.companyRef?.companyName || '-',
      width: 350,
    },
    {
      key: 'totalNet',
      title: messages['common.net'],
      dataIndex: 'totalNet',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalVat',
      title: messages['common.vat'],
      dataIndex: 'totalVat',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalPrices',
      title: messages['common.total'],
      dataIndex: 'totalPrices',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (data) =>
        formatMoney(data, showCurrencyByCountry(payrollCountry)),
    },
    ...((invoiceType === InvoiceTypeEnum.INVOICE && !isBureau
      ? [
          {
            key: 'receipt',
            title: messages['common.paid'],
            dataIndex: 'receipt',
            align: 'right',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            render: (value) =>
              formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
          },
          {
            key: 'outstanding',
            title: messages['common.outstanding'],
            dataIndex: 'outstanding',
            align: 'right',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            render: (data) =>
              formatMoney(data, showCurrencyByCountry(payrollCountry)),
          },
        ]
      : []) as ColumnsType<Invoice>),
  ];
};
